import {useMemo, useState} from "react";
import {App as AntdApp, Alert, Input, InputNumber, Table, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAddKeywordItemsMutation, useGetKeywordExploreQuery} from "~/reduxs/api/keywordApiSlice";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import ConnectGscCta from "./ConnectGscCta";
import {metricsColumns, useMetricsLookup} from "./keywordMetrics";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

/**
 * Tab "Khám phá": toàn bộ query mà site đã hiển thị trên Google (gộp từ GSC DB, mặc định
 * 90 ngày) — impressions là nhu cầu tìm kiếm THẬT, tin cậy hơn "search volume" ước lượng.
 * Lọc theo chuỗi/impressions/vị trí; chọn nhiều dòng hoặc bấm nhanh để lưu vào Kho từ khoá.
 * Dự án chưa kết nối GSC → CTA sang trang Search Console.
 */
function KeywordExplorer({projectId}) {

	const {notification} = AntdApp.useApp();

	// Bộ lọc nhập tự do — chỉ áp khi bấm "Lọc" (mỗi tổ hợp là 1 cache key RTK riêng).
	const [q, setQ] = useState('');
	const [minImpr, setMinImpr] = useState(null);
	const [posMin, setPosMin] = useState(null);
	const [posMax, setPosMax] = useState(null);
	const [applied, setApplied] = useState({});

	const {data, isLoading, isFetching} = useGetKeywordExploreQuery({projectId, ...applied});
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();
	const {data: sub} = useGetMySubscriptionQuery();

	const rows = useMemo(() => data?.rows || [], [data]);
	const [selectedKeys, setSelectedKeys] = useState([]);

	// Volume/CPC/KD (DataForSEO §8): cache BE join sẵn vào row (0đ); nút tra sẽ xếp hàng
	// các từ cache miss (trừ quota tháng) rồi poll — kết quả mới merge đè theo keyword.
	const {run: runLookup, rows: lookupRows, lookingUp} = useMetricsLookup(projectId, notification);
	const canLookup = !!sub?.is_root || (sub?.keyword_lookups?.limit ?? 0) > 0;

	const resolveMetrics = (r) => {
		const looked = lookupRows[r.keyword];
		return {
			metrics: (looked?.metrics) || r.metrics || null,
			pending: looked?.status === 'pending',
		};
	};

	const apply = () => {
		setApplied({
			...(q.trim() ? {q: q.trim()} : {}),
			...(minImpr ? {min_impr: minImpr} : {}),
			...(posMin ? {pos_min: posMin} : {}),
			...(posMax ? {pos_max: posMax} : {}),
		});
		setSelectedKeys([]);
	};

	const save = async (list) => {
		if (!list.length) return;
		try {
			const res = await addItems({
				projectId,
				items: list.map((r) => ({keyword: r.keyword, source: 'gsc', impressions: r.impressions, position: r.position})),
			}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: `${res.added} từ khoá mới${res.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
			} else {
				notification.info({message: 'Đã có', description: 'Các từ khoá này đã có trong kho.'});
			}
			setSelectedKeys([]);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		}
	};

	if (isLoading) return <Loading />;

	if (data && data.connected === false) {
		return <ConnectGscCta projectId={projectId} message="Tab Khám phá dùng dữ liệu tìm kiếm thật từ Google Search Console — dự án này chưa kết nối." />;
	}

	const columns = [
		{
			title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true,
			render: (v, r) => (
				<span>
					{v}{' '}
					{r.saved && (
						<Tooltip title="Đã có trong kho từ khoá">
							<FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a'}} />
						</Tooltip>
					)}
				</span>
			),
		},
		{
			// Cột phụ — ẩn dưới 768px cho gọn mobile.
			title: 'Clicks', dataIndex: 'clicks', key: 'clicks', width: 100, sorter: (a, b) => a.clicks - b.clicks,
			responsive: ['md'],
			render: (v) => v.toLocaleString('vi-VN'),
		},
		{
			title: 'Impressions', dataIndex: 'impressions', key: 'impressions', width: 130,
			sorter: (a, b) => a.impressions - b.impressions, defaultSortOrder: 'descend',
			render: (v) => v.toLocaleString('vi-VN'),
		},
		{
			// Cột phụ — ẩn dưới 768px cho gọn mobile.
			title: 'CTR', dataIndex: 'ctr', key: 'ctr', width: 90, sorter: (a, b) => a.ctr - b.ctr,
			responsive: ['md'],
			render: (v) => `${(v * 100).toFixed(1)}%`,
		},
		{
			title: 'Vị trí', dataIndex: 'position', key: 'position', width: 90, sorter: (a, b) => a.position - b.position,
			render: (v) => Number(v).toFixed(1),
		},
		// Volume / CPC / KD từ DataForSEO (cache chung — bấm "Tra volume" để lấy từ còn thiếu).
		...metricsColumns(resolveMetrics),
		{
			title: '', key: 'action', align: 'right', width: 60,
			render: (_, r) => (
				<Tooltip title={r.saved ? 'Đã có trong kho' : 'Lưu vào kho từ khoá'}>
					<Button small white disabled={r.saved || saving} onClick={() => save([r])}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
				</Tooltip>
			),
		},
	];

	const exportRows = () => rows.map((r) => {
		const m = resolveMetrics(r).metrics;
		return [r.keyword, r.clicks, r.impressions, +(r.ctr * 100).toFixed(2), r.position, m?.volume ?? '', m?.cpc ?? '', m?.kd ?? ''];
	});
	const rangeText = data?.range ? `${data.range.from} → ${data.range.to}` : '';
	const exportHead = ['Từ khoá', 'Clicks', 'Impressions', 'CTR (%)', 'Vị trí TB', 'Volume', 'CPC ($)', 'KD'];

	const buildExcel = () => [{
		name: 'Khám phá từ khoá',
		aoa: [exportHead, ...exportRows()],
	}];

	const buildPdf = () => ({
		title: 'Khám phá từ khoá (Google Search Console)',
		subtitle: rangeText,
		sections: [{columns: exportHead, rows: exportRows()}],
	});

	const selectedRows = rows.filter((r) => selectedKeys.includes(r.keyword) && !r.saved);

	// Tra volume/CPC/KD: theo dòng đã chọn, không chọn gì thì tra toàn bộ danh sách (trần 200/lần).
	const lookupTargets = (selectedKeys.length > 0 ? rows.filter((r) => selectedKeys.includes(r.keyword)) : rows)
		.map((r) => r.keyword);

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Impressions là số lần site hiển thị THẬT trên Google (từ Search Console) — đáng tin hơn 'search volume' ước lượng, nhưng chỉ có với từ khoá site đã từng hiển thị." />

			<div className={cn('filterBar') + ' form'}>
				<Input
					style={{width: 260}} allowClear
					placeholder="Từ khoá chứa…"
					value={q} onChange={(e) => setQ(e.target.value)} onPressEnter={apply}
				/>
				<InputNumber style={{width: 140}} min={0} placeholder="Impressions ≥" value={minImpr} onChange={setMinImpr} />
				<InputNumber style={{width: 110}} min={1} max={100} placeholder="Vị trí từ" value={posMin} onChange={setPosMin} />
				<InputNumber style={{width: 110}} min={1} max={100} placeholder="đến" value={posMax} onChange={setPosMax} />
				<Button primary small onClick={apply} leftIcon={<FontAwesomeIcon icon="fa-light fa-filter" />}>Lọc</Button>
				<span className={cn('hint')}>
					{rows.length.toLocaleString('vi-VN')} từ khoá
					{data?.limit && rows.length >= data.limit ? ` (hiển thị tối đa ${data.limit} theo impressions)` : ''}
					{rangeText ? ` · ${rangeText}` : ''}
				</span>
				<div style={{marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center'}}>
					{canLookup && rows.length > 0 && (
						<Tooltip title="Lấy volume/CPC/độ khó cho các từ đã chọn (không chọn = cả danh sách, tối đa 200/lần). Từ đã có sẽ KHÔNG tốn lượt.">
							<Button small white loading={lookingUp} onClick={() => runLookup(lookupTargets)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-column" />}>
								Tra volume{selectedKeys.length > 0 ? ` (${selectedKeys.length})` : ''}
							</Button>
						</Tooltip>
					)}
					<ExportButtons filename={`kham-pha-tu-khoa-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
				</div>
			</div>

			{selectedRows.length > 0 && (
				<div className={cn('actionBar')}>
					<Button primary small loading={saving} onClick={() => save(selectedRows)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />}>
						Lưu {selectedRows.length} từ khoá đã chọn vào kho
					</Button>
				</div>
			)}

			<Table
				rowKey="keyword"
				size="small"
				loading={isFetching}
				columns={columns}
				dataSource={rows}
				rowSelection={{
					selectedRowKeys: selectedKeys,
					onChange: setSelectedKeys,
					getCheckboxProps: (r) => ({disabled: r.saved}),
				}}
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}}
			/>
		</div>
	);
}

export default KeywordExplorer;
